import { Injectable } from '@angular/core';
import { CaiApi } from '../../utils/cai-api';
import { FilterType, TimeRangeType } from '../../enum';
import {
  CarrierEvolution,
  Co2EfficiencyScore,
  Co2PerKg,
  co2Statistics,
  ForwarderMain,
  GreenestOptionUsage,
  SearchBehaviour,
  TopDestination,
  TopTradeLanes,
} from '../../model/dashboard.modal';
import { IRevenueAggregate } from '../../model/revenue.model';

import { API_CORE } from '../../constant/api-core.const';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  async getAirlinesRevenue(
    startDate: Date,
    endDate: Date,
  ): Promise<IRevenueAggregate[]> {
    const queryStringParams = {
      startingDate: startDate.toISOString().substring(0, 10),
      endingDate: endDate.toISOString().substring(0, 10),
    };
    return CaiApi.get(API_CORE.name, API_CORE.URLs.Analytics.AirlinesRevenue, {
      queryParams: queryStringParams,
    });
  }

  async getForwardersRevenue(
    startDate: Date,
    endDate: Date,
  ): Promise<IRevenueAggregate[]> {
    const queryStringParams = {
      startingDate: startDate.toISOString().substring(0, 10),
      endingDate: endDate.toISOString().substring(0, 10),
    };
    return CaiApi.get(
      API_CORE.name,
      API_CORE.URLs.Analytics.ForwardersRevenue,
      {
        queryParams: queryStringParams,
      },
    );
  }

  async getForwardersMain(
    startDate: string,
    endDate: string,
    previousStartDate: string,
    previousEndDate: string,
    airline: string,
    officeId: number,
  ): Promise<ForwarderMain[]> {
    const queryStringParams = {
      startingDate: startDate.substring(0, 10),
      endingDate: endDate.substring(0, 10),
      previousStartDate: previousStartDate.substring(0, 10),
      previousEndDate: previousEndDate.substring(0, 10),
      airline,
      officeId,
    };
    return CaiApi.get(API_CORE.name, API_CORE.URLs.Analytics.ForwardersMain, {
      queryParams: queryStringParams,
    });
  }

  async getTopDestinations(
    startDate: string,
    endDate: string,
    type: FilterType,
    officeId: number,
  ): Promise<TopDestination[]> {
    const queryStringParams = {
      startingDate: startDate.substring(0, 10),
      endingDate: endDate.substring(0, 10),
      type,
      officeId,
    };
    return CaiApi.get(API_CORE.name, API_CORE.URLs.Analytics.TopDestinations, {
      queryParams: queryStringParams,
    });
  }

  async getSearchBehaviour(
    startDate: string,
    endDate: string,
    officeId: number,
  ): Promise<SearchBehaviour> {
    const queryStringParams = {
      startingDate: startDate.substring(0, 10),
      endingDate: endDate.substring(0, 10),
      officeId,
    };
    return CaiApi.get(API_CORE.name, API_CORE.URLs.Analytics.SearchBehaviour, {
      queryParams: queryStringParams,
    });
  }

  async getTopTradeLanes(
    startDate: string,
    endDate: string,
    officeId: number,
  ): Promise<TopTradeLanes[]> {
    const queryStringParams = {
      startingDate: startDate.substring(0, 10),
      endingDate: endDate.substring(0, 10),
      officeId,
    };
    return CaiApi.get(API_CORE.name, API_CORE.URLs.Analytics.TopTradeLanes, {
      queryParams: queryStringParams,
    });
  }

  async getCo2EfficiencyScore(
    startDate: string,
    endDate: string,
    timeFilter: TimeRangeType,
    officeId: number,
  ): Promise<Co2EfficiencyScore[]> {
    const queryStringParams = {
      startingDate: startDate.substring(0, 10),
      endingDate: endDate.substring(0, 10),
      timeFilter,
      officeId,
    };
    return CaiApi.get(
      API_CORE.name,
      API_CORE.URLs.Analytics.Co2EfficiencyScore,
      {
        queryParams: queryStringParams,
      },
    );
  }

  async getIndustryAverageCo2EfficiencyScore(
    startDate: string,
    endDate: string,
    timeFilter: TimeRangeType,
  ): Promise<Co2EfficiencyScore[]> {
    const queryStringParams = {
      startingDate: startDate.substring(0, 10),
      endingDate: endDate.substring(0, 10),
      timeFilter,
    };
    return CaiApi.get(
      API_CORE.name,
      API_CORE.URLs.Analytics.IndustryAverageCo2EfficiencyScore,
      {
        queryParams: queryStringParams,
      },
    );
  }

  async getCarrierEvolution(
    startDate: string,
    endDate: string,
    type: FilterType,
    timeFilter: TimeRangeType,
    officeId: number,
  ): Promise<CarrierEvolution[]> {
    const queryStringParams = {
      startingDate: startDate.substring(0, 10),
      endingDate: endDate.substring(0, 10),
      type,
      timeFilter,
      officeId,
    };
    return CaiApi.get(API_CORE.name, API_CORE.URLs.Analytics.BookingTrend, {
      queryParams: queryStringParams,
    });
  }

  async getGreenestOptionUsage(
    startDate: string,
    endDate: string,
    timeFilter: TimeRangeType,
    officeId: number,
  ): Promise<GreenestOptionUsage[]> {
    const queryStringParams = {
      startingDate: startDate.substring(0, 10),
      endingDate: endDate.substring(0, 10),
      timeFilter,
      officeId,
    };
    return CaiApi.get(
      API_CORE.name,
      API_CORE.URLs.Analytics.GreenestOptionUsage,
      {
        queryParams: queryStringParams,
      },
    );
  }

  async getCo2Statistics(
    startDate: string,
    endDate: string,
    previousStartDate: string,
    previousEndDate: string,
    officeId: number,
    airportCode: string,
  ): Promise<co2Statistics> {
    const queryStringParams = {
      startingDate: startDate.substring(0, 10),
      endingDate: endDate.substring(0, 10),
      previousStartDate: previousStartDate.substring(0, 10),
      previousEndDate: previousEndDate.substring(0, 10),
      officeId,
      airport: airportCode,
    };
    return CaiApi.get(API_CORE.name, API_CORE.URLs.Analytics.Co2Statistics, {
      queryParams: queryStringParams,
    });
  }

  async getCo2PerKg(
    startDate: string,
    endDate: string,
    timeFilter: TimeRangeType,
    officeId: number,
  ): Promise<Co2PerKg[]> {
    const queryStringParams = {
      startingDate: startDate.substring(0, 10),
      endingDate: endDate.substring(0, 10),
      timeFilter,
      officeId,
    };
    return CaiApi.get(API_CORE.name, API_CORE.URLs.Analytics.Co2PerKg, {
      queryParams: queryStringParams,
    });
  }

  async getRateAndCapacity(
    type: string,
    route: string,
    token?: string,
  ): Promise<Array<{ [key: string]: string }>> {
    const queryStringParams = {
      type,
      route,
    } as any;
    if (token) {
      queryStringParams.token = token;
    }
    return CaiApi.get(API_CORE.name, API_CORE.URLs.Analytics.CapacityAndRate, {
      queryParams: queryStringParams,
    });
  }

  async getQualityScore(
    route: string,
  ): Promise<Array<{ [key: string]: string }>> {
    const queryStringParams = {
      route,
    };
    return CaiApi.get(API_CORE.name, API_CORE.URLs.Analytics.QualityScore, {
      queryParams: queryStringParams,
    });
  }
}
